import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { getPedidosWorkDelivery, exportarPedidosWorkDeliveryExcel } from "../../services/api";

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString || "-";
  }
};

const formatCurrency = (value) => {
  if (value == null || value === "") return "-";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;
  return new Intl.NumberFormat("es-PY").format(numericValue);
};

const formatQuantity = (value) => {
  if (value == null || value === "") return "-";
  return new Intl.NumberFormat("es-PY", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const getNombreUsuario = (pedido) => {
  const firstName = pedido?.usuario?.first_name || "";
  const lastName = pedido?.usuario?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || "-";
};

const getCedulaUsuario = (pedido) => pedido?.usuario?.cedula || "-";

const getLineTotal = (detalle) => {
  const precioUnitario = Number(detalle?.precio_descuento ?? detalle?.precio);
  const cantidad = Number(detalle?.cantidad);

  if (Number.isNaN(precioUnitario) || Number.isNaN(cantidad)) {
    return null;
  }

  return precioUnitario * cantidad;
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    fecha_desde: formatDateInput(firstDay),
    fecha_hasta: formatDateInput(lastDay),
  };
};

const createDefaultFilters = () => {
  const { fecha_desde, fecha_hasta } = getCurrentMonthDateRange();

  return {
    nropedido: "",
    ruc_empresa: "",
    cedula: "",
    fecha_desde,
    fecha_hasta,
  };
};

const filterLabels = {
  nropedido: "Nro. pedido",
  ruc_empresa: "RUC empresa",
  cedula: "Cédula",
  fecha_desde: "Fecha desde",
  fecha_hasta: "Fecha hasta",
};

const sanitizeFilters = (filters) =>
  Object.entries(filters).reduce((acc, [key, value]) => {
    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue) {
      acc[key] = normalizedValue;
    }

    return acc;
  }, {});

const PedidosWorkDelivery = () => {
  const defaultFilters = useMemo(() => createDefaultFilters(), []);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [modalPedido, setModalPedido] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [validationError, setValidationError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const hasFetchedInitially = useRef(false);

  const fetchPedidos = useCallback(async (currentFilters) => {
    const isInitialRequest = !hasFetchedInitially.current;

    if (isInitialRequest) {
      setLoading(true);
    } else {
      setIsFetching(true);
    }

    setError(null);

    try {
      const response = await getPedidosWorkDelivery(currentFilters);
      setPedidos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err);
    } finally {
      if (isInitialRequest) {
        setLoading(false);
        hasFetchedInitially.current = true;
      } else {
        setIsFetching(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPedidos(appliedFilters);
  }, [appliedFilters, fetchPedidos]);

  const handleVerPedido = (pedido) => setModalPedido(pedido);
  const handleCerrarModal = () => setModalPedido(null);
  const handleFilterChange = ({ target: { name, value } }) => {
    setValidationError("");
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    const normalizedFilters = sanitizeFilters(filters);

    if (
      normalizedFilters.fecha_desde &&
      normalizedFilters.fecha_hasta &&
      normalizedFilters.fecha_desde > normalizedFilters.fecha_hasta
    ) {
      setValidationError("La fecha desde no puede ser mayor que la fecha hasta.");
      return;
    }

    setValidationError("");
    setAppliedFilters(normalizedFilters);
  };

  const handleClearFilters = () => {
    setValidationError("");
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleFilterKeyDown = (event) => {
    if (event.key === "Enter") {
      handleApplyFilters();
    }
  };

  const handleExportarExcel = async () => {
    setIsExporting(true);
    try {
      const response = await exportarPedidosWorkDeliveryExcel(appliedFilters);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pedidos_workdelivery_${appliedFilters.fecha_desde || "inicio"}_${appliedFilters.fecha_hasta || "fin"}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently ignore
    } finally {
      setIsExporting(false);
    }
  };

  const activeFilters = useMemo(
    () =>
      Object.entries(appliedFilters).filter(([, value]) => Boolean(value)),
    [appliedFilters]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={1.5}
        mb={3}
      >
        <Box>
          <Typography variant="h4" sx={{ mt: 0, fontWeight: 700 }}>
            Work Delivery - Pedidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consultá pedidos por número, empresa, cédula o rango de fechas.
          </Typography>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 2,
            minWidth: { xs: "100%", md: "auto" },
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Resultados
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {pedidos.length}
            </Typography>
            {isFetching && <CircularProgress size={18} />}
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtros
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Combiná los criterios para acotar la búsqueda de pedidos.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Nro. pedido"
                name="nropedido"
                value={filters.nropedido}
                onChange={handleFilterChange}
                onKeyDown={handleFilterKeyDown}
                placeholder="Ej. 464478"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="RUC empresa"
                name="ruc_empresa"
                value={filters.ruc_empresa}
                onChange={handleFilterChange}
                onKeyDown={handleFilterKeyDown}
                placeholder="Ej. 80049625-6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Cédula"
                name="cedula"
                value={filters.cedula}
                onChange={handleFilterChange}
                onKeyDown={handleFilterKeyDown}
                placeholder="Ej. 2536208"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Fecha desde"
                name="fecha_desde"
                type="date"
                value={filters.fecha_desde}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Fecha hasta"
                name="fecha_hasta"
                type="date"
                value={filters.fecha_hasta}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              Las fechas usan formato `YYYY-MM-DD` y podés combinar todos los filtros.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button variant="outlined" onClick={handleClearFilters} disabled={isFetching}>
                Limpiar
              </Button>
              <Button variant="contained" onClick={handleApplyFilters} disabled={isFetching}>
                Buscar
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={handleExportarExcel}
                disabled={isFetching || isExporting}
              >
                {isExporting ? "Exportando..." : "Exportar Excel"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {validationError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {validationError}
        </Alert>
      )}

      {activeFilters.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {`Filtros activos: ${activeFilters
            .map(([key, value]) => `${filterLabels[key] || key}: ${value}`)
            .join(" | ")}`}
        </Alert>
      )}

      {error ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <ReportProblemOutlinedIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            Error al cargar los pedidos.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verificá los filtros aplicados e intentá nuevamente.
          </Typography>
        </Box>
      ) : pedidos.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <HelpOutlineIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            No hay pedidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Probá ajustar o limpiar los filtros para ampliar la búsqueda.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid lightgray",
            borderRadius: "8px",
            backgroundColor: "#fff",
            overflowX: "auto",
            position: "relative",
          }}
        >
          {isFetching && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>Nro. pedido</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Fecha</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>RUC empresa</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Usuario</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Cédula</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Teros error</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Mensaje Teros</TableCell>
                <TableCell style={{ fontWeight: "bold" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pedidos.map((pedido) => (
                <TableRow
                  key={pedido.nro}
                  sx={{
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  <TableCell>{pedido.nropedido ?? "-"}</TableCell>
                  <TableCell>{formatDate(pedido.fecha_creacion)}</TableCell>
                  <TableCell>{pedido.ruc_empresa || "-"}</TableCell>
                  <TableCell>{getNombreUsuario(pedido)}</TableCell>
                  <TableCell>{getCedulaUsuario(pedido)}</TableCell>
                  <TableCell sx={{ color: pedido.teros_error ? "error.main" : "success.main", fontWeight: 500 }}>
                    {pedido.teros_error ? "Sí" : "No"}
                  </TableCell>
                  <TableCell>{pedido.teros_message || "-"}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleVerPedido(pedido)}
                      size="small"
                      title="Ver pedido"
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(modalPedido)}
        onClose={handleCerrarModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        {modalPedido && (
          <>
            <DialogTitle>Pedido #{modalPedido.nropedido}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Datos generales
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Usuario:</strong> {getNombreUsuario(modalPedido)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Cédula:</strong> {getCedulaUsuario(modalPedido)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Teléfono:</strong> {modalPedido.usuario?.telephone || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Email:</strong> {modalPedido.usuario?.email || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>RUC empresa:</strong> {modalPedido.ruc_empresa || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Razón social:</strong> {modalPedido.razon_social || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Sucursal:</strong> {modalPedido.nom_sucursal || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Método de pago:</strong> {modalPedido.metodo_pago || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Fecha creación:</strong> {formatDate(modalPedido.fecha_creacion)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Teros error:</strong> {modalPedido.teros_error ? "Sí" : "No"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Mensaje Teros:</strong> {modalPedido.teros_message || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Observación:</strong> {modalPedido.observacion_general || "-"}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Productos
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell>Unidad</TableCell>
                      <TableCell align="right">P. unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(modalPedido.detalles ?? []).map((detalle) => (
                      <React.Fragment key={detalle.id}>
                        <TableRow>
                          <TableCell>{detalle.nombre_producto || "-"}</TableCell>
                          <TableCell>{detalle.codigo_producto || "-"}</TableCell>
                          <TableCell align="right">{formatQuantity(detalle.cantidad)}</TableCell>
                          <TableCell>{detalle.codigo_unidad || "-"}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(detalle.precio_descuento ?? detalle.precio)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(getLineTotal(detalle))}</TableCell>
                        </TableRow>
                        {detalle.observacion_usuario && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0.5, borderBottom: "1px solid rgba(224, 224, 224, 1)" }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  fontStyle: "italic",
                                  display: "block",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  lineHeight: 1.2,
                                  pb: 1,
                                }}
                              >
                                {detalle.observacion_usuario}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={handleCerrarModal} variant="contained">
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default PedidosWorkDelivery;
